import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NotificationHub from '../components/NotificationHub';
import MyOrders from '../components/MyOrders';
import { API_BASE_URL } from '../config/api';
import './css/UserCenter.css';

function UserCenter() {
  const [selectedOption, setSelectedOption] = useState('my-cats');
  const [selectedAccountOption, setSelectedAccountOption] = useState('change-password');
  const [showPopup, setShowPopup] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [shippingAddress, setShippingAddress] = useState('');
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchShippingAddress();
    // Set selected option based on hash
    const hash = location.hash.replace('#', '');
    if (hash) {
      setSelectedOption(hash);
    }
  }, [location.hash]);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const getPageTitle = () => {
    switch (selectedOption) {
      case 'my-cats':
        return '我的猫咪';
      case 'shipping-address':
        return '邮寄地址';
      case 'my-orders':
        return '我的订单';
      case 'account-settings':
        return '账号设置';
      default:
        return '用户中心';
    }
  };

  const handleNotificationDismiss = (id) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  };

  // Helper function to get CSRF token
  const getCsrfToken = () => {
    return document.cookie.split('; ')
      .find(row => row.startsWith('csrftoken='))
      ?.split('=')[1] || '';
  };

  const fetchShippingAddress = async () => {
    try {
      const csrftoken = getCsrfToken();
      const response = await fetch(`${API_BASE_URL}/api/user-profile/`, {
        method: 'GET',
        credentials: 'include', // Include cookies in the request
        headers: {
          'X-CSRFToken': csrftoken,
        }
      });
      if (response.ok) {
        const data = await response.json();
        setShippingAddress(data.shipping_address || '');
      }
    } catch (error) {
      console.error('Error fetching shipping address:', error);
    }
  };

  const handleDeleteAccount = () => {
    setShowPopup(true);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    
    if (!oldPassword) {
      setPasswordError('请输入当前密码');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('两次输入的密码不一致');
      return;
    }
    
    if (newPassword.length < 8) {
      setPasswordError('密码长度至少为8个字符');
      return;
    }
    
    try {
      const csrftoken = getCsrfToken();
      const response = await fetch(`${API_BASE_URL}/api/change-password/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken,
        },
        credentials: 'include',
        body: JSON.stringify({ 
          old_password: oldPassword,
          new_password: newPassword 
        }),
      });
      
      if (response.ok) {
        setNotifications((prev) => [
          ...prev,
          { id: Date.now(), msgText: '密码已成功更新。' },
        ]);
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const data = await response.json();
        setNotifications((prev) => [
          ...prev,
          { id: Date.now(), msgText: `更新密码失败: ${data.error || '请稍后再试'}` },
        ]);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), msgText: '更新密码时发生错误，请稍后再试。' },
      ]);
    }
  };

  const confirmDeleteAccount = async () => {
    try {
      const csrftoken = getCsrfToken();
      const response = await fetch(`${API_BASE_URL}/delete-account/`, {
        method: 'DELETE',
        credentials: 'include', // Include cookies in the request
        headers: {
          'X-CSRFToken': csrftoken,
        }
      });

      if (response.ok) {
        // No need to remove token from localStorage as we're using cookies
        localStorage.removeItem('username');
        window.dispatchEvent(new Event('loginStatusChanged'));
        
        // Show success modal instead of navigating away immediately
        setShowSuccessModal(true);
        
        // Add a notification as well
        setNotifications((prev) => [
          ...prev,
          { id: Date.now(), msgText: '您的账号已成功注销。' },
        ]);
      } else {
        const data = await response.json();
        setNotifications((prev) => [
          ...prev,
          { id: Date.now(), msgText: `注销账号失败: ${data.error}` },
        ]);
      }
    } catch (error) {
      console.error('Error:', error);
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), msgText: '注销账号时发生错误，请稍后再试。' },
      ]);
    }
    setShowPopup(false);
  };

  const handleShippingAddressSubmit = async (e) => {
    e.preventDefault();
    try {
      const csrftoken = getCsrfToken();
      const response = await fetch(`${API_BASE_URL}/api/user-profile/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken,
        },
        credentials: 'include', // Include cookies in the request
        body: JSON.stringify({ shipping_address: shippingAddress }),
      });
      if (response.ok) {
        setNotifications((prev) => [
          ...prev,
          { id: Date.now(), msgText: '邮寄地址已成功更新。' },
        ]);
      } else {
        setNotifications((prev) => [
          ...prev,
          { id: Date.now(), msgText: '更新邮寄地址失败，请稍后再试。' },
        ]);
      }
    } catch (error) {
      console.error('Error updating shipping address:', error);
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), msgText: '更新邮寄地址时发生错误，请稍后再试。' },
      ]);
    }
  };

  const handleNavigation = (option) => {
    setSelectedOption(option);
    if (option === 'my-cats') {
      navigate('/my-cats');
    } else {
      navigate(`/user-center#${option}`);
    }
  };

  const renderContent = () => {
    switch (selectedOption) {
      case 'my-cats':
        return <div>我的猫咪 内容</div>;
      case 'shipping-address':
        return (
          <div className="shipping-address-container">
            <form onSubmit={handleShippingAddressSubmit} className="shipping-address-form">
              <div className="form-group">
                <label htmlFor="shippingAddress">您的邮寄地址：</label>
                <textarea
                  id="shippingAddress"
                  value={shippingAddress}
                  onChange={(e) => setShippingAddress(e.target.value)}
                  placeholder="请输入您的详细邮寄地址"
                  rows="4"
                />
              </div>
              <button type="submit" className="submit-btn">保存地址</button>
            </form>
          </div>
        );
      case 'my-orders':
        return <MyOrders />;
      case 'account-settings':
        return (
          <div className="account-settings-container">
            <div className="account-settings-sidebar">
              <ul className="account-settings-menu">
                <li 
                  className={selectedAccountOption === 'change-password' ? 'active' : ''}
                  onClick={() => setSelectedAccountOption('change-password')}
                >
                  更改密码
                </li>
                <li 
                  className={selectedAccountOption === 'delete-account' ? 'active' : ''}
                  onClick={() => setSelectedAccountOption('delete-account')}
                >
                  注销账号
                </li>
              </ul>
            </div>
            
            <div className="account-settings-content">
              {selectedAccountOption === 'change-password' && (
                <div className="account-settings-section">
                  <h3>更改密码</h3>
                  <form onSubmit={handleChangePassword} className="change-password-form">
                    <div className="form-group">
                      <label htmlFor="oldPassword">当前密码：</label>
                      <input
                        type="password"
                        id="oldPassword"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        placeholder="请输入当前密码"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="newPassword">新密码：</label>
                      <input
                        type="password"
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="请输入新密码"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="confirmPassword">确认密码：</label>
                      <input
                        type="password"
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="请再次输入新密码"
                      />
                    </div>
                    {passwordError && <div className="error-message">{passwordError}</div>}
                    <button type="submit" className="submit-btn">更新密码</button>
                  </form>
                </div>
              )}
              
              {selectedAccountOption === 'delete-account' && (
                <div className="account-settings-section">
                  <h3>注销账号</h3>
                  <p>警告：注销账号后，您的账号将被停用，您将无法再使用此账号登录，此操作不可逆。</p>
                  <button onClick={handleDeleteAccount} className="delete-account-btn">注销账号</button>
                </div>
              )}
            </div>
          </div>
        );
      default:
        return <div>请选择一个选项</div>;
    }
  };

  return (
    <div className="user-center">
      <div className="user-title">
        <h1>{getPageTitle()}</h1>
      </div>
      <div className="user-content-wrapper">
        <div className="user-content full-width">
          {renderContent()}
        </div>
      </div>
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>确认注销账号</h3>
            <p>您确定要注销您的账号吗？此操作不可逆，您的账号将被永久停用。</p>
            <div className="popup-buttons">
              <button onClick={confirmDeleteAccount} className="confirm-btn">确认注销</button>
              <button onClick={() => setShowPopup(false)} className="cancel-btn">取消</button>
            </div>
          </div>
        </div>
      )}
      
      {showSuccessModal && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>账号已注销</h3>
            <p>您的账号已成功注销。感谢您使用我们的服务。</p>
            <div className="popup-buttons">
              <button 
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/');
                }} 
                className="confirm-btn"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}
      <NotificationHub duration={1500} notifications={notifications} onNotificationDismiss={handleNotificationDismiss} />
    </div>
  );
}

export default UserCenter;
